import React from 'react'
import classNames from 'classnames'
import { isEmpty } from 'lodash'
import { IconInfoCircle } from '@tabler/icons-react'

import { Checkbox } from '@/components/forms/Checkbox'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import { FilterComponent } from '@/components/filters/types/FilterComponent'
import { SortButton } from '@/components/buttons/SortButton'

export const AdvancedTableHeader = ({
  actions,
  columns,
  orderBy,
  order,
  onRequestSort,
  updateSearch,
  stickyAction = false,
  truncate = false,
  wrapColumnContent = true,
  searchFilters = {},
  compactHeader = false,
  isBulkSelectable,
  selectedRows,
  allRowsSelected,
  reorderable,
  rows,
  filteredRows,
  loadingSearchAccessors,
  onSearchOpen,
  handleSelectAll,
  handleDeselectAll,
  dataGrid,
  disabledSelection,
  expandable,
  showDescription = false,
}) => {
  const actionsWidthCalculated = actions.length * 32
  const sharedHeadRowClasses = { 'compact-header': compactHeader }
  const noRowsVisible = isEmpty(filteredRows)
  const someRowsSelected = isBulkSelectable && !isEmpty(selectedRows)

  const createSortHandler = (accessor) => (event) => {
    onRequestSort(event, accessor)
  }

  return (
    <thead>
      <tr>
        {isBulkSelectable && (
          <th
            className={classNames('bulk-selectable-column', sharedHeadRowClasses, {
              'sticky-column': dataGrid,
            })}
          >
            <div className="advanced-table-column">
              {!noRowsVisible && (
                <div
                  className="vertical-center"
                  data-testid={allRowsSelected ? 'bulk-deselect-all' : 'bulk-select-all'}
                >
                  <InteractiveTooltip
                    content={someRowsSelected ? 'Deselect All' : 'Select All'}
                    placement="top-start"
                  >
                    <Checkbox
                      aria-label={someRowsSelected ? 'Deselect all' : 'Select all'}
                      indeterminate={!allRowsSelected && !isEmpty(selectedRows)}
                      checked={allRowsSelected}
                      onChange={someRowsSelected ? handleDeselectAll : handleSelectAll}
                      disabled={disabledSelection}
                    />
                  </InteractiveTooltip>
                </div>
              )}
            </div>
          </th>
        )}
        {(reorderable || expandable) && <th className={classNames(sharedHeadRowClasses)} />}
        {columns.map((headCell) => {
          const { isSortable = true, headerAlignment, isSearchable, searchSchema } = headCell
          const sorted = isSortable && orderBy === headCell.accessor

          return (
            <th
              key={headCell.accessor}
              className={classNames('table-header-cell', {
                ...sharedHeadRowClasses,
                'sticky-column': headCell.sticky,
                'sticky-column-selectable-offset': isBulkSelectable && dataGrid && headCell.sticky,
                'no-wrap': !wrapColumnContent,
                'full-width-row': headCell.fullWidthRow,
                'show-on-hover': !sorted,
                [`${headerAlignment}-aligned`]: headerAlignment && headerAlignment,
                collapsing: headCell.collapsing,
              })}
              style={headCell.color ? { borderBottom: `2px solid ${headCell.color}` } : {}}
              data-column={headCell.accessor}
              data-testid={`th--${headCell.accessor}`}
            >
              <div className="advanced-table-column">
                {isSearchable && (
                  <FilterComponent
                    type={!searchSchema ? 'text' : searchSchema.type}
                    schema={
                      !searchSchema ? headCell : { ...searchSchema, accessor: headCell.accessor }
                    }
                    value={searchFilters[headCell.accessor]}
                    loading={loadingSearchAccessors.includes(headCell.accessor)}
                    // Text filters only — select/multiSelect spread leftover props into Select
                    onOpen={
                      !searchSchema || searchSchema.type === 'text' ? onSearchOpen : undefined
                    }
                    updateSearch={updateSearch}
                    columnAccessor={headCell.accessor}
                    rows={rows}
                    formatOptionLabel={!searchSchema ? () => {} : searchSchema.format}
                  />
                )}

                {!reorderable && isSortable ? (
                  <SortButton
                    label={headCell.label}
                    handleClick={createSortHandler(headCell.accessor)}
                    dataType={headCell.accessor}
                    sortBy={orderBy}
                    asc={order === 'asc'}
                    data-testid={`sort-button-${headCell.accessor}`}
                  >
                    <div
                      className={classNames({ truncate })}
                      data-testid={`static-header-${headCell.accessor}`}
                    >
                      {headCell.label}
                    </div>
                  </SortButton>
                ) : (
                  <div
                    className={classNames({ truncate })}
                    data-testid={`static-header-${headCell.accessor}`}
                  >
                    {headCell.label}
                  </div>
                )}
                {showDescription && headCell.description && (
                  <InteractiveTooltip content={headCell.description} theme="light">
                    <IconInfoCircle
                      className="icon-svg "
                      data-testid={`description-icon-${headCell.accessor}`}
                    />
                  </InteractiveTooltip>
                )}
              </div>
            </th>
          )
        })}
        {!isEmpty(actions) && (
          <th
            style={{
              textAlign: 'center',
              width: actionsWidthCalculated,
              minWidth: actionsWidthCalculated,
            }}
            className={classNames({ 'sticky-action': stickyAction, 'no-wrap': !wrapColumnContent })}
          >
            <div className="advanced-table-column">Actions</div>
          </th>
        )}
      </tr>
    </thead>
  )
}
